import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  AutoAwesome,
  Check,
  Close,
  CloseFullscreen,
  ContentCopy,
  DeleteOutline,
  KeyboardArrowDown,
  OpenInFull,
  Send,
  Stop,
  WarningAmber
} from '@mui/icons-material'
import ReactMarkdown from 'react-markdown'
import AssistantChart from './AssistantChart'
import AssistantChartsModeSelector from './AssistantChartsModeSelector'
import remarkGfm from 'remark-gfm'
import { UserContext } from '../../context/UserContextProvider'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { resolveGreeting, sessionDisplayName } from '../generic/utilities/greeting_utils'
import { assistantActivityLabel } from './assistantActivityLabel'

// Jupiter's theme adds tokens MUI's default theme does not have; fall back to
// standard ones so the component also renders outside the app's ThemeProvider
const cardColor = theme => theme.palette.background.card || theme.palette.background.paper
const deepColor = theme => theme.palette.background.color_212121 || theme.palette.background.default
const accentColor = theme => theme.palette.secondary.darker || theme.palette.primary.main

const errorMessageKeys = {
  timeout: 'This is taking me too long. Please try again.',
  unavailable: 'I cannot answer right now. Please try again in a moment.',
  interrupted: 'I could not finish that answer.'
}

// Several looping animations decorate the chat; honour the system setting of
// users who ask for reduced motion
const reducedMotion = '@media (prefers-reduced-motion: reduce)'
const noMotionSx = { [reducedMotion]: { animation: 'none' } }

// Header, conversation and input fade in one after another when the panel
// opens, instead of the whole thing landing at once
const entranceSx = (open, delay) => ({
  '@keyframes assistantPanelIn': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  animation: open ? `assistantPanelIn 260ms ease-out ${delay} both` : 'none',
  ...noMotionSx
})

// Keyboard keys drawn as keys: the shortcut hint is read once, so it should look like one
const kbdSx = {
  fontFamily: 'inherit',
  fontSize: '0.75rem',
  lineHeight: 1,
  padding: '0.3rem 0.5rem',
  borderRadius: '6px',
  border: theme => `1px solid ${alpha(theme.palette.text.primary, 0.25)}`,
  borderBottomWidth: '2px',
  backgroundColor: theme => alpha(theme.palette.text.primary, 0.06),
  color: theme => theme.palette.text.secondary
}

// Mac users read ⌘, everyone else Ctrl
const isMacPlatform = () => /Mac|iPhone|iPad/.test(window.navigator.platform || window.navigator.userAgent || '')

const messageAppearSx = {
  '@keyframes assistantMessageIn': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  animation: 'assistantMessageIn 180ms ease-out',
  ...noMotionSx
}

// While the answer is being generated the avatar wears a rotating gradient ring
const AssistantAvatar = ({ active = false }) => (
  <Box
    sx={{
      position: 'relative',
      width: '1.75rem',
      height: '1.75rem',
      minWidth: '1.75rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme => alpha(theme.palette.primary.main, 0.15),
      border: theme => `1px solid ${alpha(theme.palette.primary.main, active ? 0 : 0.4)}`,
      '@keyframes assistantRingSpin': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' }
      },
      '&::before': active
        ? {
            content: '""',
            position: 'absolute',
            inset: '-2px',
            borderRadius: '50%',
            background: theme => `conic-gradient(from 0deg, transparent 0deg, ${theme.palette.primary.main} 140deg, ${accentColor(theme)} 220deg, transparent 360deg)`,
            animation: 'assistantRingSpin 1.6s linear infinite',
            zIndex: -1,
            ...noMotionSx
          }
        : undefined
    }}
  >
    <AutoAwesome sx={{ fontSize: '0.9rem', color: theme => theme.palette.primary.main }} />
  </Box>
)

const CopyAnswerButton = ({ content }) => {
  const { t_ } = useSmartTranslation()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) {
      return undefined
    }
    const timeoutId = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timeoutId)
  }, [copied])

  const handleCopy = () => {
    // Clipboard access can be denied (insecure origin, permissions); the
    // button simply does not confirm in that case
    window.navigator.clipboard?.writeText(content)
      .then(() => setCopied(true))
      .catch(() => {})
  }

  return (
    <Tooltip title={copied ? t_('Copied correctly') : t_('Copy')}>
      <IconButton
        className='assistantCopyButton'
        size='small'
        onClick={handleCopy}
        aria-label={t_('Copy')}
        sx={{
          opacity: 0,
          transition: 'opacity 150ms ease-out',
          '&:focus-visible': { opacity: 1 }
        }}
      >
        {copied
          ? <Check sx={{ fontSize: '0.9rem', color: theme => theme.palette.primary.main }} />
          : <ContentCopy sx={{ fontSize: '0.9rem' }} />}
      </IconButton>
    </Tooltip>
  )
}

const TruncatedNotice = () => {
  const { t_ } = useSmartTranslation()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.5rem' }}>
      <WarningAmber sx={{ fontSize: '0.9rem', color: theme => theme.palette.warning.main }} />
      <Typography variant='caption' sx={{ color: theme => theme.palette.warning.main }}>
        {t_('I hit my length limit: this answer is incomplete.')}
      </Typography>
    </Box>
  )
}

// Memoised on purpose: every streamed token replaces the last message and
// re-renders the list, which would re-parse the Markdown of every previous
// answer. Older turns keep their object identity, so they are skipped.
const AssistantMessage = React.memo(({ role, content, streaming = false, truncated = false, charts = [] }) => {
  const isUser = role === 'user'
  if (isUser) {
    return (
      <Box
        sx={{
          ...messageAppearSx,
          alignSelf: 'flex-end',
          maxWidth: '85%',
          marginTop: '0.75rem',
          padding: '0.5rem 0.875rem',
          borderRadius: '14px 14px 4px 14px',
          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.28)}, ${alpha(theme.palette.primary.main, 0.12)})`,
          border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
          boxShadow: theme => `0px 2px 12px ${alpha(theme.palette.primary.main, 0.12)}`
        }}
      >
        <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.875rem' }}>{content}</Typography>
      </Box>
    )
  }
  return (
    <Box
      sx={{
        ...messageAppearSx,
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'flex-end',
        maxWidth: '90%',
        '&:hover .assistantCopyButton': { opacity: 1 }
      }}
    >
      <AssistantAvatar active={streaming} />
      <Box
        sx={{
          padding: '0.5rem 0.875rem',
          borderRadius: '14px 14px 14px 4px',
          background: theme => `linear-gradient(180deg, ${alpha('#FFFFFF', 0.04)}, transparent), ${theme.palette.background.paper}`,
          border: theme => `1px solid ${theme.palette.divider}`,
          boxShadow: '0px 2px 10px #00000040',
          minWidth: 0
        }}
      >
        <AssistantMarkdown content={content} streaming={streaming} />
        {charts.map(chart => <AssistantChart key={chart.id} spec={chart} />)}
        {truncated ? <TruncatedNotice /> : null}
      </Box>
      {streaming ? null : <CopyAnswerButton content={content} />}
    </Box>
  )
})

/**
 * Shadows on the sides of a horizontally scrolling block, so a table wider
 * than the bubble reads as "there is more" instead of looking cut off. Pure
 * CSS: the covers scroll with the content (`local`) and hide each shadow when
 * that end is reached.
 */
const horizontalScrollShadows = (surface) => ({
  backgroundImage: theme => [
    `linear-gradient(to right, ${surface(theme)} 30%, ${alpha(surface(theme), 0)})`,
    `linear-gradient(to left, ${surface(theme)} 30%, ${alpha(surface(theme), 0)})`,
    `radial-gradient(farthest-side at 0 50%, ${alpha(theme.palette.common.black, 0.35)}, transparent)`,
    `radial-gradient(farthest-side at 100% 50%, ${alpha(theme.palette.common.black, 0.35)}, transparent)`
  ].join(', '),
  backgroundPosition: 'left center, right center, left center, right center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '2.5rem 100%, 2.5rem 100%, 0.875rem 100%, 0.875rem 100%',
  backgroundAttachment: 'local, local, scroll, scroll'
})

// Answers arrive as Markdown (tables, lists, bold...); render them with the
// theme's look while keeping user messages as plain text.
const markdownSx = {
  fontSize: '0.875rem',
  wordBreak: 'break-word',
  '& > :first-of-type': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
  '& p': { margin: '0.25rem 0' },
  '& ul, & ol': { margin: '0.25rem 0', paddingLeft: '1.25rem' },
  '& li': { margin: '0.125rem 0' },
  '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '0.5rem 0 0.25rem', fontSize: '0.9375rem' },
  '& table': {
    borderCollapse: 'collapse',
    display: 'block',
    overflowX: 'auto',
    margin: '0.5rem 0',
    ...horizontalScrollShadows(theme => theme.palette.background.paper)
  },
  '& th, & td': {
    border: theme => `1px solid ${theme.palette.divider}`,
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
    textAlign: 'left'
  },
  '& th': { backgroundColor: theme => deepColor(theme) },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    backgroundColor: theme => deepColor(theme),
    padding: '0.1rem 0.3rem',
    borderRadius: '4px'
  },
  '& pre': {
    backgroundColor: theme => deepColor(theme),
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    overflowX: 'auto',
    margin: '0.5rem 0',
    ...horizontalScrollShadows(deepColor)
  },
  '& pre code': { padding: 0, backgroundColor: 'transparent' },
  '& a': { color: theme => theme.palette.primary.main },
  '& blockquote': {
    borderLeft: theme => `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
    margin: '0.5rem 0',
    paddingLeft: '0.75rem',
    color: theme => theme.palette.text.secondary
  }
}

// Blinking caret at the end of the text while tokens are still arriving
const streamingCursorSx = {
  '@keyframes assistantCursorBlink': {
    '0%, 45%': { opacity: 1 },
    '55%, 100%': { opacity: 0 }
  },
  '& > *:last-child::after': {
    content: '""',
    display: 'inline-block',
    width: '0.45em',
    height: '1em',
    marginLeft: '0.15em',
    verticalAlign: 'text-bottom',
    backgroundColor: theme => theme.palette.primary.main,
    animation: 'assistantCursorBlink 1s step-end infinite',
    ...noMotionSx
  }
}

// Answers can link outside the MIC; never navigate away from the app
const markdownComponents = {
  a: ({ children, ...props }) => <a {...props} target='_blank' rel='noopener noreferrer'>{children}</a>
}

const AssistantMarkdown = ({ content, streaming = false }) => (
  <Box sx={streaming ? { ...markdownSx, ...streamingCursorSx } : markdownSx}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
  </Box>
)

const TypingDot = ({ delay }) => (
  <Box
    sx={{
      width: '0.375rem',
      height: '0.375rem',
      borderRadius: '50%',
      backgroundColor: theme => theme.palette.primary.main,
      '@keyframes assistantTypingDot': {
        '0%, 80%, 100%': { opacity: 0.25, transform: 'scale(0.75)' },
        '40%': { opacity: 1, transform: 'scale(1)' }
      },
      animation: 'assistantTypingDot 1.2s ease-in-out infinite',
      animationDelay: delay,
      ...noMotionSx
    }}
  />
)

const TypingIndicator = ({ label }) => (
  <Box sx={{ ...messageAppearSx, display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
    <AssistantAvatar active />
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.625rem 0.875rem',
        borderRadius: '14px 14px 14px 4px',
        background: theme => `linear-gradient(180deg, ${alpha('#FFFFFF', 0.04)}, transparent), ${theme.palette.background.paper}`,
        border: theme => `1px solid ${theme.palette.divider}`,
        boxShadow: '0px 2px 10px #00000040'
      }}
    >
      <Box sx={{ display: 'flex', gap: '0.25rem' }}>
        <TypingDot delay='0s' />
        <TypingDot delay='0.15s' />
        <TypingDot delay='0.3s' />
      </Box>
      <Typography
        variant='caption'
        sx={{
          // the label sweeps with a light shimmer so a long wait still feels alive
          '@keyframes assistantShimmer': {
            from: { backgroundPosition: '200% 0' },
            to: { backgroundPosition: '-200% 0' }
          },
          background: theme => `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.secondary} 35%, ${theme.palette.primary.main} 50%, ${theme.palette.text.secondary} 65%, ${theme.palette.text.secondary} 100%)`,
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          animation: 'assistantShimmer 2.5s linear infinite',
          [reducedMotion]: { animation: 'none', color: theme => theme.palette.text.secondary }
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
)

/**
 * The assistant never runs a write on its own: it proposes the action and the
 * user confirms it here. The arguments are shown because this is the last
 * chance to notice the assistant misunderstood the request.
 */
const PendingActionCard = ({ action, onConfirm, onCancel, disabled }) => {
  const { t_ } = useSmartTranslation()
  const actionArguments = Object.entries(action.arguments || {})

  return (
    <Box
      sx={{
        ...messageAppearSx,
        marginLeft: '2.25rem',
        padding: '0.75rem 0.875rem',
        borderRadius: '14px',
        backgroundColor: theme => alpha(theme.palette.warning.main, 0.08),
        border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.5)}`
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <WarningAmber sx={{ fontSize: '1.1rem', color: theme => theme.palette.warning.main }} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          {t_('I need your confirmation')}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.875rem', fontFamily: 'monospace', wordBreak: 'break-word' }}>
        {action.name}
      </Typography>
      {actionArguments.length > 0
        ? (
          <Box component='dl' sx={{ margin: '0.5rem 0 0', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.125rem 0.5rem' }}>
            {actionArguments.map(([key, value]) => (
              <React.Fragment key={key}>
                <Typography component='dt' sx={{ fontSize: '0.8125rem', color: theme => theme.palette.text.secondary }}>
                  {key}
                </Typography>
                <Typography component='dd' sx={{ fontSize: '0.8125rem', margin: 0, wordBreak: 'break-word' }}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
          )
        : null}
      <Box sx={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <Button size='small' color='inherit' onClick={onCancel} disabled={disabled}>
          {t_('Cancel')}
        </Button>
        <Button size='small' variant='contained' color='warning' onClick={onConfirm} disabled={disabled}>
          {t_('Confirm')}
        </Button>
      </Box>
    </Box>
  )
}

const EmptyState = ({ greeting, label, shortcutLabel, shortcutKeys }) => (
  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
    <Box
      sx={{
        position: 'relative',
        width: '4rem',
        height: '4rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)}, ${alpha(theme.palette.primary.main, 0.05)})`,
        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        '@keyframes assistantHaloPulse': {
          '0%': { transform: 'scale(1)', opacity: 0.5 },
          '70%': { transform: 'scale(1.6)', opacity: 0 },
          '100%': { transform: 'scale(1.6)', opacity: 0 }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: theme => `1px solid ${theme.palette.primary.main}`,
          animation: 'assistantHaloPulse 3s ease-out infinite',
          ...noMotionSx
        }
      }}
    >
      <AutoAwesome sx={{ fontSize: '1.75rem', color: theme => theme.palette.primary.main }} />
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '1.0625rem', textAlign: 'center', letterSpacing: '0.01em' }}>
        {greeting}
      </Typography>
      <Typography sx={{ color: theme => theme.palette.text.secondary, textAlign: 'center', maxWidth: '16rem', fontSize: '0.875rem' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
      <Typography variant='caption' sx={{ color: theme => theme.palette.text.secondary }}>
        {shortcutLabel}
      </Typography>
      <Box sx={{ display: 'flex', gap: '0.3rem' }}>
        {shortcutKeys.map(key => (
          <Box key={key} component='kbd' sx={kbdSx}>{key}</Box>
        ))}
      </Box>
    </Box>
  </Box>
)

// Fades the edge the conversation continues past, so history out of view is
// visible as such. Only the ends that actually have more content are faded.
const conversationMask = (fadeTop, fadeBottom) => {
  if (!fadeTop && !fadeBottom) {
    return 'none'
  }
  const stops = [fadeTop ? 'transparent 0' : '#000 0']
  if (fadeTop) {
    stops.push('#000 1.5rem')
  }
  if (fadeBottom) {
    stops.push('#000 calc(100% - 1.5rem)')
  }
  stops.push(fadeBottom ? 'transparent 100%' : '#000 100%')
  return `linear-gradient(to bottom, ${stops.join(', ')})`
}

const DEFAULT_WIDTH = 416
const WIDE_WIDTH = 760
const MIN_WIDTH = 360
const MAX_WIDTH_RATIO = 0.8

/**
 * The chat panel. The conversation state is owned by AssistantLauncher and
 * handed over in `chat`, so its button can reflect what the assistant is doing
 * while this panel is closed.
 */
const AssistantChatDrawer = ({ chat, open, onClose }) => {
  const { t_ } = useSmartTranslation()
  // The panel may mount outside the provider (tests); the greeting then falls back to its impersonal form
  const { user } = useContext(UserContext) || {}
  const greeting = resolveGreeting(t_, new Date().getHours(), sessionDisplayName(user))
  const shortcutKeys = isMacPlatform() ? ['⌘', 'K'] : ['Ctrl', 'K']
  const {
    messages,
    sending,
    error,
    errorReference,
    activity,
    preparingAction,
    pendingAction,
    sendMessage,
    retryLastTurn,
    confirmPendingAction,
    cancelPendingAction,
    clearConversation,
    cancelStreaming
  } = chat

  const handleChartsModeChange = (mode) => chat.setChartsMode(mode)
  const [input, setInput] = useState('')
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [atBottom, setAtBottom] = useState(true)
  const [atTop, setAtTop] = useState(true)
  const bottomRef = useRef(null)
  const messagesRef = useRef(null)
  const inputRef = useRef(null)

  const lastMessage = messages[messages.length - 1] || {}
  const waitingForAnswer = sending && (activity !== '' || lastMessage.content === '')
  const indicatorLabel = assistantActivityLabel(t_, { activity, preparingAction })

  const isExpanded = width > (DEFAULT_WIDTH + WIDE_WIDTH) / 2

  const clampWidth = (value) => {
    return Math.min(Math.max(value, MIN_WIDTH), Math.floor(window.innerWidth * MAX_WIDTH_RATIO))
  }

  const toggleExpanded = () => {
    setWidth(isExpanded ? DEFAULT_WIDTH : clampWidth(WIDE_WIDTH))
  }

  // Drag the drawer's left edge to resize it freely
  const handleResizeStart = (event) => {
    event.preventDefault()
    const onPointerMove = (moveEvent) => {
      setWidth(clampWidth(window.innerWidth - moveEvent.clientX))
    }
    const onPointerUp = () => {
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
    }
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
  }

  // Follow the answer only while the user is already at the end, so reading
  // back through the conversation is not interrupted by every new token
  useEffect(() => {
    if (open && atBottom && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [open, atBottom, messages, sending, error])

  const handleMessagesScroll = () => {
    const container = messagesRef.current
    if (container) {
      setAtBottom(container.scrollHeight - container.scrollTop - container.clientHeight < 40)
      setAtTop(container.scrollTop < 8)
    }
  }

  const scrollToBottom = () => {
    setAtBottom(true)
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const sendCurrentInput = () => {
    const text = input.trim()
    if (text === '' || sending) {
      return
    }
    setInput('')
    setAtBottom(true)
    sendMessage(text)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    sendCurrentInput()
  }

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendCurrentInput()
    }
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      // focus the input once the panel is in place, so it is ready to type in
      SlideProps={{ onEntered: () => inputRef.current?.focus() }}
      sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: `${width}px` },
          // frosted panel over the app, with an ambient mint glow at the top
          backgroundColor: theme => alpha(cardColor(theme), 0.88),
          backgroundImage: theme => `radial-gradient(120% 40% at 100% 0%, ${alpha(theme.palette.primary.main, 0.13)}, transparent 70%)`,
          backdropFilter: 'blur(18px)',
          borderLeft: theme => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          boxShadow: '-8px 0px 32px #00000070'
        }
      }}
    >
      <Box
        onPointerDown={handleResizeStart}
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '0.4rem',
          cursor: 'ew-resize',
          zIndex: 1,
          transition: 'background-color 150ms ease-out',
          '&:hover': { backgroundColor: theme => alpha(theme.palette.primary.main, 0.35) },
          '&:hover .assistantResizeGrip': { opacity: 1 }
        }}
      >
        <Box
          className='assistantResizeGrip'
          sx={{
            width: '0.15rem',
            height: '2.5rem',
            borderRadius: '1rem',
            opacity: 0,
            transition: 'opacity 150ms ease-out',
            backgroundColor: theme => theme.palette.primary.main
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            gap: '0.75rem',
            backgroundColor: theme => alpha(deepColor(theme), 0.75),
            ...entranceSx(open, '0ms')
          }}
        >
          <Box
            sx={{
              width: '2.25rem',
              height: '2.25rem',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme => `linear-gradient(135deg, ${theme.palette.primary.main}, ${accentColor(theme)})`,
              boxShadow: theme => `0px 0px 14px ${alpha(theme.palette.primary.main, 0.45)}`
            }}
          >
            <AutoAwesome sx={{ fontSize: '1.25rem', color: '#212121' }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, lineHeight: 1.2, letterSpacing: '0.02em' }}>{t_('Assistant')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Box
                sx={{
                  width: '0.375rem',
                  height: '0.375rem',
                  borderRadius: '50%',
                  backgroundColor: theme => theme.palette.primary.main,
                  '@keyframes assistantOnlinePulse': {
                    '0%, 100%': { boxShadow: theme => `0px 0px 0px 0px ${alpha(theme.palette.primary.main, 0.6)}` },
                    '50%': { boxShadow: theme => `0px 0px 0px 3px ${alpha(theme.palette.primary.main, 0)}` }
                  },
                  animation: 'assistantOnlinePulse 2.4s ease-out infinite',
                  ...noMotionSx
                }}
              />
              <Typography variant='caption' sx={{ color: theme => theme.palette.text.secondary }}>MIC</Typography>
            </Box>
          </Box>
          <Tooltip title={isExpanded ? t_('Collapse') : t_('Expand')}>
            <IconButton
              size='small'
              onClick={toggleExpanded}
              aria-label={isExpanded ? t_('Collapse') : t_('Expand')}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {isExpanded ? <CloseFullscreen fontSize='small' /> : <OpenInFull fontSize='small' />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t_('Clear conversation')}>
            <span>
              <IconButton size='small' onClick={clearConversation} disabled={sending || messages.length === 0} aria-label={t_('Clear conversation')}>
                <DeleteOutline fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size='small' onClick={onClose} aria-label={t_('Close')}>
            <Close fontSize='small' />
          </IconButton>
        </Box>
        <Box
          sx={{
            height: '2px',
            background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`
          }}
        />
        <Box
          ref={messagesRef}
          onScroll={handleMessagesScroll}
          role='log'
          aria-live='polite'
          aria-label={t_('Assistant')}
          sx={{
            ...entranceSx(open, '80ms'),
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '1rem',
            '& > :first-of-type': { marginTop: 0 },
            maskImage: conversationMask(!atTop, !atBottom),
            WebkitMaskImage: conversationMask(!atTop, !atBottom),
            '&::-webkit-scrollbar': { width: '0.375rem' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#55555580',
              borderRadius: '0.25rem',
              '&:hover': { backgroundColor: theme => alpha(theme.palette.primary.main, 0.5) }
            }
          }}
        >
          {messages.length === 0 && !sending
            ? (
              <EmptyState
                greeting={greeting}
                label={t_('Ask me anything about your plant')}
                shortcutLabel={t_('Open it from anywhere with the shortcut')}
                shortcutKeys={shortcutKeys}
              />
              )
            : null}
          {messages.map((message, index) => (
            message.role === 'assistant' && message.content === ''
              ? null
              : (
                <AssistantMessage
                  key={index}
                  role={message.role}
                  content={message.content}
                  truncated={message.truncated}
                  charts={message.charts}
                  streaming={sending && index === messages.length - 1}
                />
                )
          ))}
          {waitingForAnswer
            ? <TypingIndicator label={indicatorLabel} />
            : null}
          {pendingAction.id
            ? (
              <PendingActionCard
                action={pendingAction}
                onConfirm={confirmPendingAction}
                onCancel={cancelPendingAction}
                disabled={sending}
              />
              )
            : null}
          {error !== ''
            ? (
              <Alert
                severity={error === 'interrupted' ? 'warning' : 'error'}
                sx={{ ...messageAppearSx, borderRadius: '12px' }}
                action={
                  <Button color='inherit' size='small' onClick={retryLastTurn} disabled={sending}>
                    {t_('Retry')}
                  </Button>
                }
              >
                {t_(errorMessageKeys[error])}
                {errorReference !== ''
                  ? (
                    <Typography
                      variant='caption'
                      component='div'
                      sx={{ marginTop: '0.25rem', opacity: 0.8, userSelect: 'all', wordBreak: 'break-all' }}
                    >
                      {t_('Error reference')}: {errorReference}
                    </Typography>
                    )
                  : null}
              </Alert>
              )
            : null}
          <div ref={bottomRef} />
        </Box>
        {atBottom
          ? null
          : (
            <Tooltip title={t_('Go to the last message')}>
              <IconButton
                onClick={scrollToBottom}
                aria-label={t_('Go to the last message')}
                sx={{
                  position: 'absolute',
                  bottom: '5rem',
                  right: '1.25rem',
                  zIndex: 2,
                  backgroundColor: theme => theme.palette.background.paper,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  boxShadow: '0px 2px 8px #00000060',
                  '&:hover': { backgroundColor: theme => theme.palette.background.button || theme.palette.action.hover }
                }}
              >
                <KeyboardArrowDown fontSize='small' />
              </IconButton>
            </Tooltip>
            )}
        <Box
          sx={{
            ...entranceSx(open, '160ms'),
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '0.5rem 1rem 0',
            borderTop: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          <AssistantChartsModeSelector value={chat.chartsMode} onChange={handleChartsModeChange} disabled={sending} />
        </Box>
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{
            ...entranceSx(open, '160ms'),
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'flex-end',
            padding: '0.5rem 1rem 1rem'
          }}
        >
          <TextField
            fullWidth
            size='small'
            multiline
            maxRows={4}
            inputRef={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t_('Type your request...')}
            disabled={sending}
            inputProps={{ 'aria-label': t_('Assistant message') }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px',
                backgroundColor: theme => alpha(deepColor(theme), 0.8),
                fontSize: '0.875rem',
                transition: 'box-shadow 180ms ease-out',
                '&.Mui-focused': {
                  boxShadow: theme => `0px 0px 0px 3px ${alpha(theme.palette.primary.main, 0.18)}`
                }
              }
            }}
          />
          <IconButton
            type={sending ? 'button' : 'submit'}
            onClick={sending ? cancelStreaming : undefined}
            disabled={!sending && input.trim() === ''}
            aria-label={sending ? t_('Stop generating') : t_('Send')}
            sx={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '14px',
              color: '#212121',
              background: theme => `linear-gradient(135deg, ${theme.palette.primary.main}, ${accentColor(theme)})`,
              boxShadow: theme => `0px 0px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              transition: 'box-shadow 150ms ease-out, transform 150ms ease-out',
              '&:hover': {
                background: theme => `linear-gradient(135deg, ${accentColor(theme)}, ${theme.palette.primary.main})`,
                boxShadow: theme => `0px 0px 18px ${alpha(theme.palette.primary.main, 0.55)}`,
                transform: 'scale(1.05)'
              },
              '&.Mui-disabled': {
                background: theme => alpha(theme.palette.primary.main, 0.2),
                boxShadow: 'none',
                color: theme => alpha('#212121', 0.5)
              }
            }}
          >
            {sending ? <Stop fontSize='small' /> : <Send fontSize='small' />}
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  )
}

export default AssistantChatDrawer
